import {
  APIError,
  type StudioTeamAccessFilter,
  type StudioTeamAccessResponse,
  type StudioTeamActivityAction,
  type StudioTeamActivityQuery,
  type StudioTeamChange,
  type StudioTeamInvitation,
  type StudioTeamInviteResult,
  type StudioTeamOrg,
  type StudioTeamPermission,
  type StudioTeamPerson,
  type StudioTeamPersonRole,
  type StudioTeamRoleGroup,
  type StudioTeamRoleKey,
  type StudioTeamRoleSummary,
} from '@/api/adminClient';
import { formatDate } from '@/lib/format';

/** The roles as the API lists them, looked up by key. A key the page does not know is shown as itself. */
export type RoleCatalog = {
  roles: StudioTeamRoleSummary[];
  groups: StudioTeamRoleGroup[];
  byKey: Map<StudioTeamRoleKey, StudioTeamRoleSummary>;
  /** Every permission a role can carry, in display order. */
  permissions: StudioTeamPermission[];
};

export function roleCatalog(data: Pick<StudioTeamAccessResponse, 'roles' | 'roleGroups' | 'permissions'> | undefined): RoleCatalog {
  const roles = data?.roles ?? [];
  return { roles, groups: data?.roleGroups ?? [], byKey: new Map(roles.map((role) => [role.key, role])), permissions: data?.permissions ?? [] };
}

export function roleLabel(catalog: RoleCatalog, key: StudioTeamRoleKey): string {
  return catalog.byKey.get(key)?.label ?? key;
}

/** Platform Admin stays outside the bundles: shown on its own and always confirmed. */
export const PLATFORM_ADMIN_ROLE_KEY = 'platform_admin';

/** A role filter entry that matches by effective permission rather than by the role held. */
export const PERMISSION_FILTER_PREFIX = 'perm:';

/** Edit roles and Invite: the bundles first, Platform Admin on its own, the single permissions under Advanced. */
export function roleSections(catalog: RoleCatalog): { bundles: StudioTeamRoleSummary[]; platformAdmin?: StudioTeamRoleSummary; advanced: StudioTeamRoleSummary[] } {
  return {
    bundles: catalog.roles.filter((role) => role.kind === 'bundle'),
    platformAdmin: catalog.byKey.get(PLATFORM_ADMIN_ROLE_KEY),
    advanced: catalog.roles.filter((role) => role.kind !== 'bundle' && role.key !== PLATFORM_ADMIN_ROLE_KEY),
  };
}

/** A permission by its name ("Code Editor"), else by the single role that grants it, else the permission itself. */
export function permissionLabel(catalog: RoleCatalog, permission: string): string {
  return (
    catalog.permissions.find((entry) => entry.slug === permission)?.label ??
    catalog.roles.find((role) => role.kind !== 'bundle' && role.permissions.length === 1 && role.permissions[0] === permission)?.label ??
    permission
  );
}

/** The permissions in display order: the catalog's, then any a role carries that it does not list. */
export function permissionOrder(catalog: RoleCatalog): string[] {
  const listed = catalog.permissions.map((entry) => entry.slug);
  return [...new Set([...listed, ...catalog.roles.filter((role) => role.kind !== 'bundle').flatMap((role) => role.permissions)])];
}

/** Every permission these roles grant together, in display order. */
export function effectivePermissions(catalog: RoleCatalog, keys: Iterable<StudioTeamRoleKey>): string[] {
  const granted = new Set([...keys].flatMap((key) => catalog.byKey.get(key)?.permissions ?? []));
  const order = permissionOrder(catalog);
  return [...order.filter((permission) => granted.has(permission)), ...[...granted].filter((permission) => !order.includes(permission))];
}

export function effectivePermissionLabels(catalog: RoleCatalog, keys: Iterable<StudioTeamRoleKey>): string[] {
  return effectivePermissions(catalog, keys).map((permission) => permissionLabel(catalog, permission));
}

/** Single permissions held alongside a bundle that already grants them, with the bundle that does. */
export function redundantRoles(catalog: RoleCatalog, keys: Iterable<StudioTeamRoleKey>): Array<{ key: StudioTeamRoleKey; coveredBy: StudioTeamRoleKey }> {
  const held = [...keys].flatMap((key) => catalog.byKey.get(key) ?? []);
  const bundles = held.filter((role) => role.kind === 'bundle');
  return held
    .filter((role) => role.kind !== 'bundle')
    .flatMap((role) => {
      const bundle = bundles.find((candidate) => role.permissions.every((permission) => candidate.permissions.includes(permission)));
      return bundle ? [{ key: role.key, coveredBy: bundle.key }] : [];
    });
}

/** The selection with every redundant single permission taken out. */
export function withoutRedundant(catalog: RoleCatalog, selected: ReadonlySet<StudioTeamRoleKey>): Set<StudioTeamRoleKey> {
  const redundant = new Set(redundantRoles(catalog, selected).map((entry) => entry.key));
  return new Set([...selected].filter((key) => !redundant.has(key)));
}

/** "Already included in Developer Access", keyed by each redundant role. */
export function redundantNotes(catalog: RoleCatalog, keys: Iterable<StudioTeamRoleKey>): Record<StudioTeamRoleKey, string> {
  return Object.fromEntries(redundantRoles(catalog, keys).map((entry) => [entry.key, `Already included in ${roleLabel(catalog, entry.coveredBy)}.`]));
}

/** A role filter entry's label: the role, or the permission marked "(effective)". */
export function roleFilterLabel(catalog: RoleCatalog, entry: string): string {
  return entry.startsWith(PERMISSION_FILTER_PREFIX)
    ? `${permissionLabel(catalog, entry.slice(PERMISSION_FILTER_PREFIX.length))} (effective)`
    : roleLabel(catalog, entry);
}

export type AccessFilter = StudioTeamAccessFilter;

export function personLabel(person: Pick<StudioTeamPerson, 'name' | 'email' | 'userId'>): string {
  return person.name || person.email || person.userId;
}

export const SEARCH_MIN_CHARS = 2;

/** The search the server is asked for: nothing until it is long enough. */
export function searchQuery(text: string): string | undefined {
  const trimmed = text.trim();
  return trimmed.length >= SEARCH_MIN_CHARS ? trimmed : undefined;
}

export const PEOPLE_PAGE_SIZES = [25, 50] as const;

/** "Showing 1–25 of 312". */
export function rangeLabel(page: number, pageSize: number, total: number, noun = 'people'): string {
  if (total === 0) return `No ${noun}`;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return `Showing ${from.toLocaleString('en-US')}–${to.toLocaleString('en-US')} of ${total.toLocaleString('en-US')}`;
}

/** The full role change log, its own page under Users. */
export const USER_CHANGES_PATH = '/users/changes';

export const ORG_STORAGE_KEY = 'studio-admin.users.org';

export function readRememberedOrg(): string | undefined {
  try {
    return window.localStorage.getItem(ORG_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function rememberOrg(orgId: string): void {
  try {
    window.localStorage.setItem(ORG_STORAGE_KEY, orgId);
  } catch {
    return;
  }
}

/** The organization the list opens on: the one picked last in this browser, else the largest. */
export function pickOrg(orgs: StudioTeamOrg[], remembered: string | undefined): string | undefined {
  if (remembered && orgs.some((org) => org.id === remembered)) return remembered;
  return [...orgs].sort((a, b) => (b.memberCount ?? 0) - (a.memberCount ?? 0))[0]?.id;
}

/** "RapidSOS (312)" in the organization switcher. */
export function orgOptionLabel(org: StudioTeamOrg): string {
  return typeof org.memberCount === 'number' ? `${org.name} (${org.memberCount.toLocaleString('en-US')})` : org.name;
}

/** Who a change was made to: their name, email or user id. */
export function changeTarget(change: StudioTeamChange): string {
  return change.targetName || change.targetEmail || change.targetUserId || 'someone';
}

/** A change's sentence around the person it was made to: `[before, after]`, so the person can be a link. */
export function changeLineParts(change: StudioTeamChange, catalog: RoleCatalog): [string, string] {
  const by = change.actorName || change.actor;
  const role = catalog.byKey.get(change.roleKey)?.label ?? change.roleName.replace(/^Studio /, '');
  switch (change.action) {
    case 'granted':
      return change.viaInvitation ? ['', ` joined and got ${role}, invited by ${by}`] : [`${by} gave `, ` ${role}`];
    case 'removed':
      return [`${by} removed ${role} from `, ''];
    case 'invited':
      return [`${by} invited `, ` as ${role}`];
    case 'invite_revoked':
      return [`${by} withdrew the invitation giving `, ` ${role}`];
  }
}

/** One line per change: "James gave Ada Lovelace Code Editor". */
export function changeLine(change: StudioTeamChange, catalog: RoleCatalog): string {
  const [before, after] = changeLineParts(change, catalog);
  return `${before}${changeTarget(change)}${after}`;
}

/** A person's own page (`PersonPage`), or undefined when there is no user id to link (an invitation). */
export function userDetailPath(userId: string | undefined): string | undefined {
  return userId ? `/users/${encodeURIComponent(userId)}` : undefined;
}

/** What "Give … to everyone" says it will touch: only the organization shown. */
export function bulkGrantDescription(orgName: string, orgCount: number): string {
  const others = orgCount > 1 ? `Only ${orgName}: other RapidSOS organizations and people outside RapidSOS are not touched.` : 'People outside RapidSOS are not touched.';
  return `Everyone in ${orgName} who does not have it yet. ${others} Each one is recorded in Recent changes.`;
}

export const ACTIVITY_ACTIONS: Array<{ id: StudioTeamActivityAction; label: string }> = [
  { id: 'granted', label: 'Granted' },
  { id: 'removed', label: 'Removed' },
  { id: 'invited', label: 'Invited' },
  { id: 'invite_revoked', label: 'Invitation revoked' },
];

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The change log's filters, read from the page's address so a filtered view can be shared. */
export function activityQueryFrom(params: URLSearchParams): StudioTeamActivityQuery {
  const page = Number(params.get('page'));
  const text = (key: string) => params.get(key)?.trim() || undefined;
  const day = (key: string) => {
    const value = text(key);
    return value && DAY.test(value) ? value : undefined;
  };
  const action = text('action');
  return {
    page: Number.isInteger(page) && page >= 1 ? page : 1,
    ...(text('person') ? { person: text('person') } : {}),
    ...(text('role') ? { role: text('role') } : {}),
    ...(action && ACTIVITY_ACTIONS.some((entry) => entry.id === action) ? { action: action as StudioTeamActivityAction } : {}),
    ...(day('from') ? { from: day('from') } : {}),
    ...(day('to') ? { to: day('to') } : {}),
    ...(text('q') ? { q: text('q') } : {}),
  };
}

/** The address for these filters; page 1 and empty filters are left out. */
export function activityParams(query: StudioTeamActivityQuery): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of ['person', 'role', 'action', 'from', 'to', 'q'] as const) {
    const value = query[key];
    if (value) params.set(key, value);
  }
  if (query.page > 1) params.set('page', String(query.page));
  return params;
}

export function heldRole(person: StudioTeamPerson, key: StudioTeamRoleKey): StudioTeamPersonRole | undefined {
  return person.roles.find((role) => role.key === key);
}

/**
 * The row's compact summary. `main` is the person's bundles and Platform
 * Admin, the badges that say what they are; `extra` is up to `max` single
 * permissions a bundle does not already cover, then how many more;
 * `redundant` counts singles a bundle covers (offered for clean-up).
 */
export function roleSummary(
  person: StudioTeamPerson,
  catalog: RoleCatalog,
  max = 3,
): { main: string[]; extra: string[]; more: number; redundant: number; effective: string[] } {
  const order = new Map(catalog.roles.map((role, index) => [role.key, index]));
  const keys = [...person.roles].sort((left, right) => (order.get(left.key) ?? 99) - (order.get(right.key) ?? 99)).map((role) => role.key);
  const redundant = new Set(redundantRoles(catalog, keys).map((entry) => entry.key));
  const isMain = (key: string) => catalog.byKey.get(key)?.kind === 'bundle' || key === PLATFORM_ADMIN_ROLE_KEY;
  const extra = keys.filter((key) => !isMain(key) && !redundant.has(key)).map((key) => roleLabel(catalog, key));
  return {
    main: keys.filter(isMain).map((key) => roleLabel(catalog, key)),
    extra: extra.slice(0, max),
    more: Math.max(0, extra.length - max),
    redundant: redundant.size,
    effective: effectivePermissionLabels(catalog, keys),
  };
}

const BLOCKED_HINTS: Record<NonNullable<StudioTeamPersonRole['removeBlockedReason']>, string> = {
  self_lockout: "You can't remove your own Platform Admin role.",
  last_admin: 'The last Platform Admin cannot be removed. Add another one first.',
};

/** Why each role cannot be ticked or unticked for this person in Edit roles, if it cannot. */
export function editLocks(person: StudioTeamPerson, catalog: RoleCatalog): Record<StudioTeamRoleKey, string | undefined> {
  const locks: Record<StudioTeamRoleKey, string | undefined> = {};
  for (const role of catalog.roles) {
    const held = heldRole(person, role.key);
    if (held) {
      if (!held.removable) locks[role.key] = held.removeBlockedReason ? BLOCKED_HINTS[held.removeBlockedReason] : 'Cannot be removed here.';
    } else if (!role.available) {
      locks[role.key] = `WorkOS has no "${role.name}" role yet.`;
    } else if (!person.internal) {
      locks[role.key] = 'Only people in a RapidSOS organization can be given a Studio role.';
    }
  }
  return locks;
}

/** What Save sends: the roles to give and to take away. */
export function planRoleChanges(person: StudioTeamPerson, selected: ReadonlySet<StudioTeamRoleKey>): { add: StudioTeamRoleKey[]; remove: StudioTeamRoleKey[] } {
  const held = new Set(person.roles.map((role) => role.key));
  return {
    add: [...selected].filter((key) => !held.has(key)),
    remove: [...held].filter((key) => !selected.has(key)),
  };
}

/** The changes Save asks about first: every removal, and giving a role that asks (Platform Admin). */
export function changesToConfirm(plan: { add: StudioTeamRoleKey[]; remove: StudioTeamRoleKey[] }, catalog: RoleCatalog): string[] {
  return [
    ...plan.add.filter((key) => catalog.byKey.get(key)?.confirmGrant).map((key) => `Give ${roleLabel(catalog, key)}: ${catalog.byKey.get(key)?.description ?? ''}`.trim()),
    ...plan.remove.map((key) => `Remove ${roleLabel(catalog, key)}`),
  ];
}

export type CannotManage = { message: string; notConfigured: boolean };

/** The API's answer when WorkOS will not let Studio read or change roles. */
export function cannotManageRoles(error: unknown): CannotManage | undefined {
  if (!(error instanceof APIError)) return undefined;
  const code = error.body?.code;
  if (code !== 'directory_forbidden' && code !== 'directory_not_configured') return undefined;
  return { message: error.message, notConfigured: code === 'directory_not_configured' };
}

export const MAX_INVITE_EMAILS = 20;
const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

/** The invite box: addresses separated by commas, semicolons, spaces or new lines. Same rules as the API. */
export function parseEmailList(text: string): { emails: string[]; invalid: string[] } {
  const emails: string[] = [];
  const invalid: string[] = [];
  for (const part of text.split(/[\s,;]+/).map((value) => value.trim().toLowerCase()).filter(Boolean)) {
    const list = EMAIL_RE.test(part) ? emails : invalid;
    if (!list.includes(part)) list.push(part);
  }
  return { emails, invalid };
}

/** What the invite form says it will do, or why it cannot yet. */
export function inviteFormProblem(text: string, orgId: string | undefined): string | undefined {
  const { emails, invalid } = parseEmailList(text);
  if (invalid.length > 0) return `Not an email address: ${invalid.join(', ')}`;
  if (emails.length === 0) return 'Enter at least one email address.';
  if (emails.length > MAX_INVITE_EMAILS) return `Invite at most ${MAX_INVITE_EMAILS} people at a time.`;
  if (!orgId) return 'Pick the organization to invite them into.';
  return undefined;
}

function roleList(keys: StudioTeamRoleKey[], catalog: RoleCatalog): string {
  return keys.map((key) => roleLabel(catalog, key)).join(', ');
}

/** One line per address after inviting. */
export function inviteResultText(result: StudioTeamInviteResult, orgName: string, catalog: RoleCatalog): string {
  switch (result.status) {
    case 'invited':
      return result.roles.length > 0
        ? `Invited to ${orgName}. Gets ${roleList(result.roles, catalog)} once they join.`
        : `Invited to ${orgName}.`;
    case 'exists':
      return result.canAddDirectly
        ? `Already has a RapidSOS login. Add them to ${orgName} directly, or send an invitation anyway.`
        : `Already has a login, but its address isn't verified. Send an invitation instead.`;
    case 'already_member':
      if (result.rolesGiven.length > 0) return `Already in ${orgName}. Given ${roleList(result.rolesGiven, catalog)}; they apply within about a minute.`;
      return result.verified ? `Already in ${orgName}.` : `Already in ${orgName}, but their address isn't verified. Give roles from the list.`;
    case 'failed':
      return result.error;
  }
}

/** "Invited by Me · Sent 5 Oct · Expires 12 Oct", or that it expired. */
export function invitationMeta(invitation: StudioTeamInvitation): string {
  return [
    invitation.invitedBy ? `Invited by ${invitation.invitedBy}` : undefined,
    invitation.invitedAt ? `Sent ${formatDate(invitation.invitedAt)}` : undefined,
    invitation.expired ? 'Expired' : invitation.expiresAt ? `Expires ${formatDate(invitation.expiresAt)}` : undefined,
  ]
    .filter(Boolean)
    .join(' · ');
}
